import { useQuery } from '@apollo/client';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import { BillingDocPageV2, ErrorComponents, getAdaBaseURL, useTitle, withApollo } from '@css-xp/base';
import { Refund } from '@atlassian/cssxp-types-billing';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  getRefundActions,
  getRefundAdditionalDetail,
  getRefundBreadcrumbTrail,
  getRefundSubheader,
} from '../refund-page-utils';
import RefundViewAdjustmentsPane from './refund-view-adjustments-pane';
import RefundLineItems from './refund-line-items';
import { fetchRefundDetailQueryV2 } from './queries/fetch-refund-query';
import { EntityWithNotes } from '@atlassian/cssxp-types-audit';
import { removeContentScrollingHack } from '@atlassian/cssxp-utils-generic';
import { getInvoiceHeader } from '../../../invoices';
import {
  FetchRefundDetailQueryQuery,
  FetchRefundDetailQueryQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { ViewPricing } from '@css-xp/base/common-sidebars';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { UFOEventSource } from '@atlassian/temp-observability';

export enum SIDEBAR {
  VIEW_PRICING_DETAILS = 'viewPricingDetails',
  REFUND_VIEW_ADJUSTMENTS = 'refund-view-adjustments',
}

const RefundPage = ({ appPageClass, title }: { appPageClass?: string; title?: string }): JSX.Element | null => {
  useEffect(() => {
    removeContentScrollingHack();
  }, []);

  const { id } = useParams<{ id: string }>();
  useTitle(id, title);
  // TODO:  Need to update it further for presence hook but using redux logic once user profile service done
  //const { viewers } = usePresence('invoice', id);
  //const previousId = usePrevious(id);

  //to capture the pageView experience for refund page
  GlobalPageLoadExperience.startPageLoad(UFOEventSource.REFUND_SCREEN);
  React.useEffect(() => {
    GlobalPageLoadExperience.success({ force: true });
  }, []);

  const { data, loading, error, refetch } = useQuery<FetchRefundDetailQueryQuery, FetchRefundDetailQueryQueryVariables>(
    fetchRefundDetailQueryV2,
    {
      variables: {
        id,
      },
    }
  );
  if (loading) return <LoadingIndicator />;

  if (!data) {
    return (
      <ErrorComponents.ErrorPanel error={new ErrorComponents.NotFoundError(`Unable to find invoice with ID: ${id}`)} />
    );
  }
  if (error) return <ErrorComponents.GenericErrorPage error={error} />;

  const refund = ((data?.refund ? data.refund[0] : null) as unknown) as Refund;
  if (refund && !refund?.refundItems) {
    return null;
  }

  const auditTrailProps = {
    entityName: EntityWithNotes.REFUND,
    id: refund?.creditNoteId ?? id,
  };

  const adaBaseUrl = getAdaBaseURL();

  const allowedSidebars = {
    [SIDEBAR.VIEW_PRICING_DETAILS]: withApollo(ViewPricing),
    [SIDEBAR.REFUND_VIEW_ADJUSTMENTS]: RefundViewAdjustmentsPane,
  };

  return (
    <BillingDocPageV2
      headerProps={{
        getHeader: getInvoiceHeader.bind(null, refund),
        getSubheader: getRefundSubheader.bind(null, refund, undefined),
        getActions: getRefundActions.bind(null, adaBaseUrl),
        //viewers,
      }}
      onComplete={refetch}
      billingDoc={refund}
      isFetchingBillingDoc={false}
      billingDocFetchError={null}
      getBreadcrumbTrail={getRefundBreadcrumbTrail}
      auditTrailProps={auditTrailProps}
      analyticsScreenSource={AnalyticsEventSource.REFUND_SCREEN}
      additionalDetailsContent={getRefundAdditionalDetail(refund)}
      BillingDocLineItems={RefundLineItems}
      appPageClass={appPageClass + ' refund-page'}
      allowedSidebars={allowedSidebars}
    />
  );
};

export default RefundPage;
